// Multi-window mat (2026-09-23, Michael) — staff only, same rule as the quote tool
// (tpw-tools quote_form.html / generate_quote.py "Multi-window mat").
//
// The prints go in as ordinary cart lines, each priced as a print. The mat (Mat Only) or
// mat + frame (Matted and Framed) is ONE more line built on the MultiWindowMat.jpg
// placeholder (utils/constants/placeholderUploads.ts):
//   - its size IS the glass size: the mat border is priced as 0 (pricingOptions below),
//     so the backend's glass = size + 2 x mat = the size staff picked;
//   - no print is charged on it (the row's paperPrice is taken off, the same client-side
//     split "Print previously purchased" uses — stores/prevPrinted.ts);
//   - each window after the first adds $15;
//   - staff tick which cart lines go in the mat: the ticks ARE the window count, and are
//     saved on the line (itemJson.multiWindow) for the work order / cut list.
// Checkout charges the stored itemJson.price as-is, so nothing changes server-side.
import { PRODUCT_TYPE } from "@/utils/content/productTypes";
import type { VisualizerStoreProps } from "./visualizer";

export const MULTI_WINDOW_EXTRA = 15;
export const MULTI_WINDOW_PLACEHOLDER = "MultiWindowMat.jpg";

export type MultiWindowPrint = {
  lineItemId: string;
  fileName: string;
  width: number;
  height: number;
};

/** Giclée, Matted (Mat Only or Matted and Framed). */
export function isMultiWindowEligible(store: VisualizerStoreProps): boolean {
  if (store.selectedProduct !== PRODUCT_TYPE.GICLEE) return false;
  const t = String(store.options?.treatmentOptions?.type || "");
  return /matted/i.test(t) && !/gallery/i.test(t);
}

/** Box ticked on an eligible item. */
export function isMultiWindow(store: VisualizerStoreProps): boolean {
  return !!store.multiWindow && isMultiWindowEligible(store);
}

/** The options the backend prices / the line is built from: mat border 0, so size = glass. */
export function multiWindowOptions<T extends { frameOptions?: any }>(options: T, on: boolean): T {
  if (!on || !options?.frameOptions) return options;
  return {
    ...options,
    frameOptions: {
      ...options.frameOptions,
      matboard: { ...(options.frameOptions.matboard || {}), size: 0 },
    },
  };
}

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** The cart lines a multi-window mat can hold: sized print lines, not the line being edited, not another multi-window mat. */
export function multiWindowCandidates(lineItems: any[], editingItemId?: string | null): MultiWindowPrint[] {
  return (lineItems || [])
    // Unframed fine-art prints only — the only thing that goes in a mat (not canvas / aluminum, Michael 09-24).
    .filter(li => li?.id && li.id !== editingItemId && !li?.itemJson?.multiWindow && !li?.adminCustom)
    .filter(li => /fine art/i.test(String(li?.itemJson?.productType || "")) &&
      /no frame/i.test(String(li?.itemJson?.treatmentOptions?.type || "")))
    .map(li => {
      const d = li?.itemJson?.sizing?.dimensions || {};
      return {
        lineItemId: String(li.id),
        fileName: String(li?.itemJson?.fileName || li?.itemJson?.cartData?.fileName || "print"),
        width: num(d.width),
        height: num(d.height),
      };
    })
    .filter(p => p.width > 0 && p.height > 0);
}

/** Ticked prints, in the cart's order. Ticks whose line left the cart drop out. */
export function multiWindowPrints(store: VisualizerStoreProps, lineItems: any[]): MultiWindowPrint[] {
  const ids = new Set(store.multiWindowLineIds || []);
  return multiWindowCandidates(lineItems, store.editingItemId).filter(p => ids.has(p.lineItemId));
}

const fmt = (n: number) => String(parseFloat(n.toFixed(3)));

/** "8x10 ×2, 5x7" — identical sizes grouped, in order. */
export function multiWindowSizesText(prints: Pick<MultiWindowPrint, "width" | "height">[]): string {
  const g: { k: string; n: number }[] = [];
  for (const p of prints) {
    const k = `${fmt(p.width)}x${fmt(p.height)}`;
    const f = g.find(x => x.k === k);
    if (f) f.n++;
    else g.push({ k, n: 1 });
  }
  return g.map(x => (x.n > 1 ? `${x.k} ×${x.n}` : x.k)).join(", ");
}

export const MULTI_WINDOW_BORDER = 0.5;
export const MULTI_WINDOW_PRINT_NOTE = "In multi-window mat";
const OLD_NOTES = ['In multi-window mat — ½" border added'];

/**
 * A print going into a multi-window mat (Michael 09-24): every one is marked "In multi-window mat" so all the
 * prints in a mat read alike, and a full-bleed one becomes a ½" straight-cut border (never printed full bleed
 * for a mat) — the border bullet, the overall size and the saved border setting. Price unchanged (a border up
 * to 1" is free). Borders, deckled edges and customer-supplied art keep their size. Null = nothing to change.
 */
export function withMultiWindowBorder(itemJson: any): any | null {
  const po = itemJson?.paperOptions || {};
  const cd = itemJson?.cartData || {};
  const before: string[] = cd.conditionalFields || [];
  let fields = before.filter(f => OLD_NOTES.indexOf(f) < 0);
  if (fields.indexOf(MULTI_WINDOW_PRINT_NOTE) < 0) fields = fields.concat([MULTI_WINDOW_PRINT_NOTE]);
  const addBorder =
    !itemJson?.supplied && !(Number(po.size || 0) > 0) && po.style !== 2 && po.style !== 3;
  if (!addBorder) {
    if (fields.length === before.length && fields.every((f, i) => f === before[i])) return null;
    return { ...itemJson, cartData: { ...cd, conditionalFields: fields } };
  }
  const b = MULTI_WINDOW_BORDER;
  const d = itemJson?.sizing?.dimensions || {};
  const w = Number(d.width) + b * 2, h = Number(d.height) + b * 2;
  const fmt = (n: number) => String(parseFloat(n.toFixed(3)));
  return {
    ...itemJson,
    paperOptions: { ...po, size: b, style: 1 },
    overallSizing: { width: w, height: h },
    cartData: {
      ...cd,
      conditionalFields: fields.map(f => (f === "Full Bleed" ? `${b}" Border; Straight-Cut Edge` : f)),
      staticField2: String(cd.staticField2 || "").replace(
        /\(Overall Dimensions [^)]*\)/,
        `(Overall Dimensions ${fmt(w)}x${fmt(h)})`
      ),
    },
  };
}
